import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { profilePath } from '@/routes/slugs';
import apiCoreAdapter from '../../../../../hooks/apiCoreAdapter';
import { parseSessionDate, toISODate } from '../../../../../utils/sessions';
import { BA_TIMEZONE } from '../../../../../utils/timezone';
import type { SessionLite } from '../../../../../types/session-lite';
import CircularProgress from '../../../../../components/common/CircularProgress';
import { useVacationBlockade } from '../../../../../hooks/useVacationBlockade';
import { useIsPwaApp } from '../../../../../pwa/PwaModeContext';
import { VacationSegmentTooltip } from '../../../../../components/psychologist/calendar/VacationSegmentTooltip';
import { usePsychologistContext } from '../../../../../contexts/PsychologistContext';
import { useFormacionBlocks } from '../../../../../hooks/useFormacionBlocks';
import { isoToZoneParts } from '../../../../../components/psychologist/calendar/timeZoneHelpers';
import {
  FormacionBlockDialog,
  FormacionBlockHover,
  type FormacionBlockData,
} from '../../../../../components/psychologist/calendar/FormacionBlockInfo';

const WEEKDAY_HEADERS_FULL = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const WEEKDAY_HEADERS_SHORT = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

function buildMonthGrid(year: number, month: number): Date[][] {
  const firstDay = new Date(year, month, 1);
  // JS: 0=Sun … 6=Sat → Mon-based: Mon=0 … Sun=6
  const dow = firstDay.getDay();
  const mondayBased = dow === 0 ? 6 : dow - 1;

  const cells: Date[] = [];

  // Preceding days from previous month
  for (let i = mondayBased - 1; i >= 0; i--) {
    cells.push(new Date(year, month, -i));
  }

  // Days of the current month
  const lastDayNum = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= lastDayNum; d++) {
    cells.push(new Date(year, month, d));
  }

  // Trailing days to fill the last row
  const remainder = cells.length % 7;
  if (remainder !== 0) {
    for (let d = 1; d <= 7 - remainder; d++) {
      cells.push(new Date(year, month + 1, d));
    }
  }

  const weeks: Date[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  return weeks;
}

function getHourLabel(dateStr: string, timeZone: string = BA_TIMEZONE): string {
  const d = parseSessionDate(dateStr);
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone,
  }).format(d).replace('24:', '00:');
}

function formatPopoverTitle(iso: string, timeZone: string = BA_TIMEZONE): string {
  const d = new Date(`${iso}T12:00:00`);
  const parts = new Intl.DateTimeFormat('es-AR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone,
  }).formatToParts(d);
  const weekday = parts.find((p) => p.type === 'weekday')?.value ?? '';
  const day = parts.find((p) => p.type === 'day')?.value ?? '';
  const month = parts.find((p) => p.type === 'month')?.value ?? '';
  const capitalised = weekday.charAt(0).toUpperCase() + weekday.slice(1);
  return `${capitalised} ${day} de ${month}`;
}

interface MonthViewProps {
  currentMonthDate: Date;
  onNavigateToDay: (dateISO: string) => void;
  onSessionClick?: (session: SessionLite) => void;
  isMobile?: boolean;
  /** Called when user swipes left (→ next month) or right (→ prev month) */
  onSwipeNext?: () => void;
  onSwipePrev?: () => void;
  /** Timezone of the psychologist — used to place/format sessions. */
  therapistTimezone?: string;
}

interface PopoverState {
  iso: string;
  top: number;
  left: number;
  openAbove: boolean;
}

export const MonthView: React.FC<MonthViewProps> = ({
  currentMonthDate,
  onNavigateToDay,
  onSessionClick,
  isMobile = false,
  onSwipeNext,
  onSwipePrev,
  therapistTimezone = BA_TIMEZONE,
}) => {
  const year = currentMonthDate.getFullYear();
  const month = currentMonthDate.getMonth();
  const weeks = buildMonthGrid(year, month);
  const flat = weeks.flat();
  const totalCells = flat.length;

  const [sessionsByDay, setSessionsByDay] = useState<Record<string, SessionLite[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [popover, setPopover] = useState<PopoverState | null>(null);
  const navigate = useNavigate();
  const { psychologist } = usePsychologistContext();
  const { getVacationSegment } = useVacationBlockade(psychologist?.id);
  // La app mobile no tiene Perfil: los avisos de vacaciones se ven pero no llevan a ningún lado.
  const isPwaApp = useIsPwaApp();
  const goToVacationsProfile = useCallback(() => {
    if (!isPwaApp) navigate(profilePath('vacations'));
  }, [navigate, isPwaApp]);

  const todayISO = toISODate(new Date());

  // Bloqueos creados al anotarse a una formación de Comunidad: esta vista no carga bloqueos por su
  // cuenta, así que se piden aparte para todo el rango visible y se agrupan por día (en el huso
  // horario de la psicóloga). Cada uno muestra título, hora y link de Meet al pasar el mouse.
  const formacionRange = React.useMemo(
    () => ({
      from: new Date(weeks[0][0].getTime() - 36 * 60 * 60_000),
      to: new Date(weeks[weeks.length - 1][6].getTime() + 60 * 60 * 60_000),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [year, month],
  );
  const formacionBlocks = useFormacionBlocks(formacionRange.from, formacionRange.to);
  const formacionByDay = React.useMemo(() => {
    const byDay: Record<string, FormacionBlockData[]> = {};
    for (const b of formacionBlocks) {
      const day = isoToZoneParts(b.startIso, therapistTimezone).date;
      (byDay[day] ??= []).push(b);
    }
    return byDay;
  }, [formacionBlocks, therapistTimezone]);
  const [openFormacionBlock, setOpenFormacionBlock] = useState<FormacionBlockData | null>(null);

  // Swipe handling for mobile month navigation
  const swipeTouchRef = useRef<{ x: number; y: number } | null>(null);
  // Stores a ref to each "+X más" trigger button keyed by ISO date
  const triggerRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    const fromISO = toISODate(weeks[0][0]);
    const toISO = toISODate(weeks[weeks.length - 1][6]);

    apiCoreAdapter
      .psychologistSessionsByDay({
        from: `${fromISO} 00:00:00`,
        to: `${toISO} 23:59:59`,
        status: 'active,incomplete,settled,cancelled',
      })
      .then((response) => {
        if (cancelled || !response || typeof response !== 'object') return;
        const byDay: Record<string, SessionLite[]> = {};
        Object.values(response).forEach((daySessions) => {
          if (!Array.isArray(daySessions)) return;
          (daySessions as SessionLite[]).forEach((s) => {
            const localDate = parseSessionDate(s.date).toLocaleDateString('en-CA', {
              timeZone: therapistTimezone,
            });
            if (!byDay[localDate]) byDay[localDate] = [];
            byDay[localDate].push(s);
          });
        });
        if (!cancelled) setSessionsByDay(byDay);
      })
      .catch(() => {
        if (!cancelled) setSessionsByDay({});
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  // Close popover on click outside or Escape
  useEffect(() => {
    if (!popover) return;
    const onMouseDown = (e: MouseEvent) => {
      const trigger = triggerRefs.current.get(popover.iso);
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        trigger !== e.target &&
        !trigger?.contains(e.target as Node)
      ) {
        setPopover(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPopover(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [popover]);

  const openPopover = useCallback((iso: string) => {
    const btn = triggerRefs.current.get(iso);
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const POPOVER_H = 260; // estimated max height
    const openAbove = rect.bottom + POPOVER_H > window.innerHeight;
    const rawLeft = rect.left;
    const POPOVER_W = 240;
    const left = Math.min(rawLeft, window.innerWidth - POPOVER_W - 8);
    setPopover({
      iso,
      top: openAbove ? rect.top - 8 : rect.bottom + 6,
      left: Math.max(8, left),
      openAbove,
    });
  }, []);

  const weekdayHeaders = isMobile ? WEEKDAY_HEADERS_SHORT : WEEKDAY_HEADERS_FULL;
  // Mobile: show max 1 session card; desktop: max 3
  const maxVisible = isMobile ? 1 : 3;

  // Popover sessions for the open day
  const popoverSessions =
    popover && sessionsByDay[popover.iso]
      ? sessionsByDay[popover.iso]
          .filter((s) => !s.isCancelled)
          .sort((a, b) => parseSessionDate(a.date).getTime() - parseSessionDate(b.date).getTime())
          .slice(maxVisible)
      : [];

  return (
    <div
      data-testid="month-view"
      data-loading={isLoading ? 'true' : undefined}
      className="relative"
      onTouchStart={isMobile ? (e) => {
        const t = e.touches[0];
        swipeTouchRef.current = { x: t.clientX, y: t.clientY };
      } : undefined}
      onTouchEnd={isMobile ? (e) => {
        if (!swipeTouchRef.current) return;
        const t = e.changedTouches[0];
        const dx = t.clientX - swipeTouchRef.current.x;
        const dy = t.clientY - swipeTouchRef.current.y;
        swipeTouchRef.current = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        if (dx < 0) onSwipeNext?.();
        else onSwipePrev?.();
      } : undefined}
    >
      {/* Loading overlay — pointer-events-none so underlying cells remain clickable */}
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 rounded pointer-events-none">
          <CircularProgress />
        </div>
      )}

      {/* Weekday column headers */}
      <div className="grid grid-cols-7 border-b border-gray-200">
        {weekdayHeaders.map((h) => (
          <div
            key={h}
            data-testid={`month-header-${h}`}
            className={cn(
              'py-2.5 text-center font-medium text-[var(--textos-gris,#455375)]',
              isMobile ? 'text-xs' : 'text-[0.8125rem]',
            )}
          >
            {h}
          </div>
        ))}
      </div>

      {/* Month grid */}
      <div className="grid grid-cols-7 border border-t-0 border-gray-200">
        {flat.map((day, idx) => {
          const iso = toISODate(day);
          const isCurrentMonth = day.getMonth() === month;
          const isToday = iso === todayISO;
          // Sort sessions by start time ascending before rendering
          const daySessions = (sessionsByDay[iso] || [])
            .filter((s) => !s.isCancelled)
            .sort((a, b) => parseSessionDate(a.date).getTime() - parseSessionDate(b.date).getTime());
          const total = daySessions.length;
          const isLastCol = (idx % 7) === 6;
          const isLastRow = idx >= totalCells - 7;
          const vacationSegment = getVacationSegment(iso);

          // ── MOBILE CELL — compact dot design (Google Calendar style) ──────────
          if (isMobile) {
            const dotCount = Math.min(total, 3);
            const hasExtraDots = total > 3;
            return (
              <button
                key={idx}
                type="button"
                data-testid={`month-cell-${iso}`}
                data-month={isCurrentMonth ? 'current' : 'adjacent'}
                data-vacation-blocked={vacationSegment ? vacationSegment.type : undefined}
                title={vacationSegment?.tooltip}
                onClick={() => onNavigateToDay(iso)}
                className={cn(
                  'flex flex-col items-center justify-start pt-1.5 pb-2',
                  vacationSegment?.type === 'vacation' ? 'bg-gray-500/35' : 'bg-white',
                  'border-0 cursor-pointer transition-colors active:bg-gray-50',
                  !isLastCol && 'border-r border-gray-200',
                  !isLastRow && 'border-b border-gray-200',
                )}
                style={{ minHeight: '56px' }}
              >
                {/* Day number */}
                <span
                  data-testid={isToday ? 'today-circle' : `month-day-number-${iso}`}
                  className={cn(
                    'inline-flex items-center justify-center w-7 h-7 rounded-full text-sm leading-none',
                    isToday
                      ? 'bg-[var(--lila,#6D57BC)] text-white font-bold'
                      : isCurrentMonth
                      ? 'font-medium text-[var(--textos-azul,#001959)]'
                      : 'font-normal text-gray-400',
                  )}
                >
                  {day.getDate()}
                </span>

                {/* Formación bloqueada ese día: marca discreta (en mobile no hay hover; el detalle está en la vista del día) */}
                {(formacionByDay[iso]?.length ?? 0) > 0 && (
                  <span
                    data-testid={`month-formacion-mark-${iso}`}
                    title={formacionByDay[iso][0].reason}
                    className="mt-1 block h-[3px] w-4 rounded bg-[#6D57BC]/60"
                  />
                )}

                {/* Session dots: max 3 circles + "+" if more */}
                {total > 0 && (
                  <div
                    data-testid={`month-dots-${iso}`}
                    className="flex items-center mt-1"
                    style={{ gap: '3px' }}
                  >
                    {Array.from({ length: dotCount }, (_, i) => (
                      <span
                        key={i}
                        className="rounded-full inline-block"
                        style={{
                          width: '6px',
                          height: '6px',
                          background: 'var(--lila, #6D57BC)',
                          flexShrink: 0,
                        }}
                      />
                    ))}
                    {hasExtraDots && (
                      <span
                        data-testid={`month-dots-extra-${iso}`}
                        style={{
                          fontSize: '0.6rem',
                          lineHeight: 1,
                          color: 'var(--lila, #6D57BC)',
                          fontWeight: 700,
                        }}
                      >
                        +
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          }

          // ── DESKTOP CELL — full detail (hours, names, popover) ────────────────
          const visible = daySessions.slice(0, maxVisible);
          const hasMore = total > maxVisible;

          const vacationBadge = vacationSegment && (
            <button
              type="button"
              data-testid={`month-vacation-badge-${iso}`}
              onClick={(e) => { e.stopPropagation(); goToVacationsProfile(); }}
              className="mb-1 block w-full px-2 py-0.5 rounded text-[0.65rem] font-bold text-white truncate text-left border-0 cursor-pointer hover:brightness-110 transition-[filter]"
              style={{ background: '#6D57BC' }}
            >
              {vacationSegment.label}
            </button>
          );

          const dayCell = (
            <div
              data-testid={`month-cell-${iso}`}
              data-month={isCurrentMonth ? 'current' : 'adjacent'}
              data-vacation-blocked={vacationSegment ? vacationSegment.type : undefined}
              // Click en el gris (no en el día/sesiones/badge, que ya cortan la propagación con
              // su propio stopPropagation) también navega al detalle de días off en Perfil.
              onClick={vacationSegment?.type === 'vacation' ? goToVacationsProfile : undefined}
              className={cn(
                'transition-colors',
                vacationSegment?.type === 'vacation' ? 'bg-gray-500/35 cursor-pointer' : 'bg-white',
                'min-h-[120px] p-2',
                !isLastCol && 'border-r border-gray-200',
                !isLastRow && 'border-b border-gray-200',
              )}
            >
              {/* Misma leyenda que en vista semanal cuando el día está bloqueado por vacaciones —
                  clickeable: lleva al cartel real ("Bloqueada para recibir pacientes nuevos") en Perfil.
                  Para 'vacation' el hover ya lo cubre toda la celda (ver abajo, wrapea `dayCell`
                  entero) — envolver también el badge acá duplicaría el tooltip. */}
              {vacationSegment?.type === 'vacation'
                ? vacationBadge
                : vacationSegment && vacationBadge && (
                    <VacationSegmentTooltip segment={vacationSegment}>
                      {vacationBadge}
                    </VacationSegmentTooltip>
                  )}
              {/* ZONA 1 — Número del día: clickeable → navega a Vista del Día */}
              <div className="flex items-start justify-between mb-0.5">
                <span
                  data-testid={isToday ? 'today-circle' : `month-day-number-${iso}`}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); onNavigateToDay(iso); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNavigateToDay(iso); } }}
                  className={cn(
                    'inline-flex items-center justify-center rounded-full cursor-pointer',
                    'hover:ring-2 hover:ring-[var(--lila,#6D57BC)] hover:ring-offset-1 transition-shadow',
                    'w-7 h-7 text-[0.8125rem]',
                    isToday
                      ? 'bg-primary text-white font-bold'
                      : isCurrentMonth
                      ? 'font-medium text-[var(--textos-azul,#001959)]'
                      : 'font-normal text-gray-400',
                  )}
                >
                  {day.getDate()}
                </span>

                {total > 0 && (
                  <span
                    data-testid={`session-badge-${iso}`}
                    className="font-semibold bg-emerald-500 text-white rounded-full leading-none whitespace-nowrap text-[0.65rem] px-1.5 py-0.5"
                  >
                    {`${total} ${total === 1 ? 'Sesión' : 'Sesiones'}`}
                  </span>
                )}
              </div>

              {/* Bloqueos de formación del día: tooltip con título, hora y link; click abre el cartel */}
              {(formacionByDay[iso] ?? []).map((b) => (
                <FormacionBlockHover key={`${b.reason}-${b.startIso}`} data={b}>
                  <div
                    data-testid="month-formacion-block"
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); setOpenFormacionBlock(b); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setOpenFormacionBlock(b); } }}
                    className="mb-0.5 cursor-pointer truncate rounded border-l-2 border-[#6D57BC] bg-[#E5E2EF] px-1 text-[0.72rem] font-semibold text-[#2D2049]"
                  >
                    {isoToZoneParts(b.startIso, therapistTimezone).time} · {b.reason}
                  </div>
                </FormacionBlockHover>
              ))}

              {/* ZONA 2 — Session cards: clickeables → abren popup de detalle */}
              {visible.map((s) => (
                <div
                  key={s.id}
                  data-testid={`month-session-${s.id}`}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); onSessionClick?.(s); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSessionClick?.(s); } }}
                  className={cn(
                    'text-[var(--textos-gris,#455375)] mb-0.5 truncate rounded px-0.5',
                    'cursor-pointer transition-colors text-[0.72rem]',
                    'hover:bg-[var(--lila-lighter,#f3efff)] hover:text-[var(--lila,#6D57BC)]',
                  )}
                >
                  <strong>{getHourLabel(s.date, therapistTimezone)}</strong>
                  {s.patientName ? ` - ${s.patientName}` : ''}
                </div>
              ))}

              {/* ZONA 3 — "+X más": clickeable → abre popover con sesiones restantes */}
              {hasMore && (
                <button
                  type="button"
                  ref={(el) => {
                    if (el) triggerRefs.current.set(iso, el);
                    else triggerRefs.current.delete(iso);
                  }}
                  data-testid={`month-more-${iso}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (popover?.iso === iso) {
                      setPopover(null);
                    } else {
                      openPopover(iso);
                    }
                  }}
                  className={cn(
                    'block mt-0.5 bg-transparent border-0 p-0 cursor-pointer text-[0.72rem]',
                    'text-[var(--lila,#6D57BC)] font-semibold',
                    'hover:underline hover:text-[var(--lila-dark,#665090)]',
                  )}
                >
                  +{total - maxVisible} más
                </button>
              )}
            </div>
          );

          // El tramo 'vacation' pinta toda la celda de gris — el hover que dispara el tooltip
          // tiene que cubrir esa misma área entera, no solo el badge (a diferencia de 'pre'/'post',
          // que no tienen relleno gris y donde alcanza con envolver el badge más arriba).
          return vacationSegment?.type === 'vacation' ? (
            <VacationSegmentTooltip key={idx} segment={vacationSegment}>
              {dayCell}
            </VacationSegmentTooltip>
          ) : (
            <React.Fragment key={idx}>{dayCell}</React.Fragment>
          );
        })}
      </div>

      {/* Popover — rendered in a portal so it's never clipped by the grid */}
      {popover &&
        createPortal(
          <div
            ref={popoverRef}
            data-testid="month-more-popover"
            role="dialog"
            aria-modal="false"
            aria-label={`Sesiones del ${formatPopoverTitle(popover.iso, therapistTimezone)}`}
            style={{
              position: 'fixed',
              top: popover.openAbove ? undefined : popover.top,
              bottom: popover.openAbove ? window.innerHeight - popover.top : undefined,
              left: popover.left,
              zIndex: 2000,
              minWidth: '240px',
              maxWidth: '320px',
              background: '#fff',
              borderRadius: '8px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.13)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
            }}
          >
            {/* Popover header */}
            <div
              style={{
                padding: '10px 14px 8px',
                borderBottom: '1px solid #f3f4f6',
                fontSize: '0.8rem',
                fontWeight: 600,
                color: 'var(--textos-azul, #001959)',
              }}
            >
              {formatPopoverTitle(popover.iso, therapistTimezone)}
            </div>

            {/* Session list */}
            <div style={{ padding: '4px 0' }}>
              {popoverSessions.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`month-popover-session-${s.id}`}
                  onClick={() => {
                    setPopover(null);
                    onSessionClick?.(s);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    background: 'none',
                    border: 'none',
                    padding: '7px 14px',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    color: 'var(--textos-gris, #455375)',
                    borderTop: i > 0 ? '1px solid #f3f4f6' : undefined,
                    transition: 'background 0.12s',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--lila-lighter, #f3efff)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'none'; }}
                >
                  <strong style={{ color: 'var(--lila, #6D57BC)' }}>{getHourLabel(s.date, therapistTimezone)}</strong>
                  {s.patientName ? ` - ${s.patientName}` : ''}
                </button>
              ))}
            </div>
          </div>,
          document.body,
        )}

      <FormacionBlockDialog data={openFormacionBlock} onClose={() => setOpenFormacionBlock(null)} />
    </div>
  );
};
